import React, { useState, useEffect, useRef } from 'react';
import './ExamInviteModal.css';

/**
 * ExamInviteModal — lets an admin copy the exam link and/or send it via email.
 *
 * Props:
 *   cert       {object}   — { id, name, code }
 *   apiUrl     {string}   — API base URL
 *   authToken  {string}   — Bearer token for admin API calls
 *   onClose    {function}
 */
function ExamInviteModal({ cert, apiUrl, getToken, onClose }) {
  const isQuizMode = cert.mode === 'quiz';
  const examUrl = isQuizMode
    ? `${window.location.origin}${window.location.pathname}?quiz=${cert.id}`
    : `${window.location.origin}${window.location.pathname}?exam=${cert.id}`;
  const modalTitle = isQuizMode ? 'Quiz Link' : 'Exam Link';
  const modalIcon = isQuizMode ? '🎯' : '🎓';
  const emailSubjectPrefix = isQuizMode ? 'Practice Quiz' : 'Practice Exam';

  const [copied, setCopied] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { sent, errors }
  const [userSearch, setUserSearch] = useState('');
  const inputRef = useRef(null);

  // Load registered users for the picker
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${apiUrl}/api/admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setRegisteredUsers((data.users || []).filter(u => u.email));
        }
      } catch { /* ignore */ }
      setUsersLoading(false);
    })();
  }, [apiUrl, getToken]);

  const copyLink = () => {
    navigator.clipboard.writeText(examUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const addEmail = (email) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(trimmed)) return;
    if (!selectedEmails.includes(trimmed)) {
      setSelectedEmails(prev => [...prev, trimmed]);
    }
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail(emailInput);
      setEmailInput('');
    }
    if (e.key === 'Backspace' && !emailInput && selectedEmails.length > 0) {
      setSelectedEmails(prev => prev.slice(0, -1));
    }
  };

  const handleEmailBlur = () => {
    if (emailInput.trim()) {
      addEmail(emailInput);
      setEmailInput('');
    }
  };

  const toggleUser = (email) => {
    setSelectedEmails(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  const removeEmail = (email) => {
    setSelectedEmails(prev => prev.filter(e => e !== email));
  };

  const handleSend = async () => {
    if (selectedEmails.length === 0) return;
    setSending(true);
    setSendResult(null);
    try {
      const token = await getToken();
      const res = await fetch(`${apiUrl}/api/admin/send-exam-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          certId: cert.id,
          certName: cert.name,
          certCode: cert.code,
          recipients: selectedEmails,
          message: message.trim() || undefined,
          mode: cert.mode || 'exam',
        }),
      });
      const data = await res.json();
      setSendResult(data);
    } catch (err) {
      setSendResult({ error: err.message });
    }
    setSending(false);
  };

  const filteredUsers = registeredUsers.filter(u =>
    !userSearch || u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <div className="invite-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="invite-modal" role="dialog" aria-modal="true" aria-label="Exam Link">
        <div className="invite-header">
          <div>
            <h2 className="invite-title">{modalIcon} {modalTitle}</h2>
            <p className="invite-subtitle">{cert.code} — {cert.name}{isQuizMode ? ' · 15 questions' : ''}</p>
          </div>
          <button className="invite-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* URL row */}
        <div className="invite-url-row">
          <input
            className="invite-url-input"
            value={examUrl}
            readOnly
            onClick={e => e.target.select()}
            aria-label="Exam URL"
          />
          <button className="invite-copy-btn" onClick={copyLink}>
            {copied ? '✓ Copied' : '📋 Copy'}
          </button>
        </div>

        <div className="invite-divider"><span>Send via email</span></div>

        {/* Registered users picker */}
        <div className="invite-section">
          <label className="invite-label">Registered users</label>
          {usersLoading ? (
            <p className="invite-loading">Loading users…</p>
          ) : (
            <>
              <input
                className="invite-user-search"
                placeholder="Search by email…"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
              />
              <div className="invite-user-list">
                {filteredUsers.length === 0 && (
                  <p className="invite-empty">No registered users found.</p>
                )}
                {filteredUsers.map(u => (
                  <label key={u.userId} className="invite-user-row">
                    <input
                      type="checkbox"
                      checked={selectedEmails.includes(u.email)}
                      onChange={() => toggleUser(u.email)}
                    />
                    <span className="invite-user-email">{u.email}</span>
                    {u.status === 'CONFIRMED' && <span className="invite-user-badge">✓</span>}
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Free-type email input */}
        <div className="invite-section">
          <label className="invite-label">Add email addresses</label>
          <div
            className="invite-email-chips"
            onClick={() => inputRef.current?.focus()}
          >
            {selectedEmails.map(email => (
              <span key={email} className="invite-chip">
                {email}
                <button
                  className="invite-chip-remove"
                  onClick={() => removeEmail(email)}
                  aria-label={`Remove ${email}`}
                >×</button>
              </span>
            ))}
            <input
              ref={inputRef}
              className="invite-chip-input"
              placeholder={selectedEmails.length === 0 ? 'Type email, press Enter or comma…' : ''}
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              onKeyDown={handleEmailKeyDown}
              onBlur={handleEmailBlur}
              type="email"
            />
          </div>
          <p className="invite-hint">Press Enter or comma to add. Multiple addresses supported.</p>
        </div>

        {/* Optional message */}
        <div className="invite-section">
          <label className="invite-label">Personal message <span className="invite-optional">(optional)</span></label>
          <textarea
            className="invite-message"
            placeholder="Add a note to include in the email…"
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={3}
          />
        </div>

        {/* Send result */}
        {sendResult && (
          <div className={`invite-result ${sendResult.error || sendResult.errors?.length > 0 ? 'result-error' : 'result-success'}`}>
            {sendResult.error ? (
              <span>❌ {sendResult.error}</span>
            ) : (
              <>
                {sendResult.sent?.length > 0 && (
                  <span>✅ Sent to {sendResult.sent.length} recipient{sendResult.sent.length !== 1 ? 's' : ''}</span>
                )}
                {sendResult.errors?.length > 0 && (
                  <span> · ❌ Failed: {sendResult.errors.map(e => e.email).join(', ')}</span>
                )}
              </>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="invite-actions">
          <button className="invite-cancel-btn" onClick={onClose}>Cancel</button>
          <button
            className="invite-send-btn"
            onClick={handleSend}
            disabled={selectedEmails.length === 0 || sending}
          >
            {sending ? 'Sending…' : `Send Invite${selectedEmails.length > 0 ? ` (${selectedEmails.length})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExamInviteModal;
